/**
 * Car images service — CRUD for the multi-image gallery (car_images table).
 *
 * ISOLATED, additive module. It does NOT change the existing `cars` logic; the
 * cover image URL is still mirrored onto `car.photo` by the caller for backward
 * compatibility with every place that already reads `car.photo`.
 *
 * An "image item" used by the UI is one of:
 *   - persisted: { id, url, isCover, order }
 *   - pending:   { tempId, file, url (objectURL for preview), isCover, order, pending:true }
 */
import { supabase, uploadToStorage } from '../../../config/supabase';

/** Map a DB row → UI item. */
const rowToItem = (r) => ({
  id: r.id,
  url: r.image_url,
  isCover: !!r.is_cover,
  order: r.display_order ?? 0,
});

/** List a single car's images, ordered (cover-aware). */
export async function listCarImages(carId) {
  if (!carId) return [];
  const { data, error } = await supabase
    .from('car_images')
    .select('*')
    .eq('car_id', carId)
    .order('display_order', { ascending: true });
  if (error) { console.warn('[carImages] list failed:', error.message); return []; }
  return (data || []).map(rowToItem);
}

/**
 * List images for many cars at once → { [carId]: item[] }.
 * Used by the reservation page and car lists. Tries the public RPC first (works
 * for anonymous visitors), then falls back to a direct select.
 */
export async function listImagesForAgency(agencyId, carIds = []) {
  const grouped = {};
  const push = (r) => {
    const cid = r.car_id;
    (grouped[cid] = grouped[cid] || []).push(rowToItem(r));
  };
  try {
    if (agencyId) {
      const { data, error } = await supabase
        .rpc('get_agency_public_car_images', { p_agency: agencyId });
      if (!error && data) { data.forEach(push); return grouped; }
    }
  } catch (_) { /* fall through */ }
  // Fallback: direct select (relies on public-read RLS).
  try {
    let q = supabase.from('car_images').select('*').order('display_order', { ascending: true });
    if (carIds && carIds.length) q = q.in('car_id', carIds);
    else if (agencyId) q = q.eq('agency_id', agencyId);
    const { data } = await q;
    (data || []).forEach(push);
  } catch (e) { console.warn('[carImages] agency list failed:', e?.message); }
  return grouped;
}

/**
 * Persist the full desired image set for a car.
 * Strategy: upload any pending files, then replace all rows for this car with
 * the provided ordered list. Simple and race-free for the small counts involved.
 *
 * @returns {Promise<{ items: object[], coverUrl: string }>}
 */
export async function saveCarImages(carId, agencyId, items = []) {
  if (!carId) throw new Error('carId requis pour enregistrer les images');

  // 1) Upload pending files → real URLs (keep order).
  const resolved = [];
  for (let i = 0; i < items.length; i++) {
    const it = items[i];
    let url = it.url;
    if (it.file) {
      url = await uploadToStorage(it.file, 'cars');
    }
    if (!url) continue;
    resolved.push({ url, isCover: !!it.isCover, order: i });
  }

  // Guarantee exactly one cover (first item by default).
  if (resolved.length && !resolved.some((r) => r.isCover)) resolved[0].isCover = true;
  let coverSeen = false;
  resolved.forEach((r) => {
    if (r.isCover && !coverSeen) coverSeen = true;
    else r.isCover = false;
  });

  // 2) Replace rows for this car.
  await supabase.from('car_images').delete().eq('car_id', carId);

  if (resolved.length) {
    const rows = resolved.map((r) => ({
      car_id: carId,
      agency_id: agencyId || null,
      image_url: r.url,
      display_order: r.order,
      is_cover: r.isCover,
    }));
    const { error } = await supabase.from('car_images').insert(rows);
    if (error) throw new Error(error.message);
  }

  const cover = resolved.find((r) => r.isCover);
  return {
    items: resolved.map((r) => ({ url: r.url, isCover: r.isCover, order: r.order })),
    coverUrl: cover ? cover.url : (resolved[0]?.url || ''),
  };
}

/** Delete every image row for a car (used if a car is removed). */
export async function deleteCarImages(carId) {
  if (!carId) return;
  try { await supabase.from('car_images').delete().eq('car_id', carId); } catch (_) {}
}
